// The KANBAN axis (brief §5.2, TaskBoard precedent): one working column per lifecycle state in the
// projection's declaration order — an unknown state key renders LAST, never dropped (G-182; the
// ordering lives in board-model.orderedBoardStates) — then the two exit columns. Per-column counts
// come from whole-board `countsByState`, NEVER from counting the filtered cards. The board scrolls
// horizontally inside its own region so the page body never does. `data-column` marks working
// columns only (exits carry `data-exit`), so tests can assert the derived order.

import type { WorkBoardProjection, WorkItemRow } from '../../projections.ts';
import { groupItemsByState, stateLabel } from '../board-model.ts';
import { BoardItemCard } from './BoardItemCard.tsx';
import { ExitColumn } from './ExitColumn.tsx';

function WorkingColumn({
  state,
  count,
  cards,
  onOpenCard,
}: {
  readonly state: string;
  readonly count: number;
  readonly cards: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label={`${stateLabel(state)} — ${String(count)} on the board`}
      data-column={state}
      className="w-64 shrink-0 rounded-md border border-border bg-surface-sunken"
    >
      <div className="flex items-baseline justify-between border-b border-border px-4 py-3">
        <span className="ritona-numeric text-xs uppercase tracking-eyebrow text-text-muted">
          {stateLabel(state)}
        </span>
        <span data-count={count} className="ritona-numeric text-xs text-text-subtle">
          {count}
        </span>
      </div>
      <div className="flex flex-col gap-2 p-2">
        {cards.length === 0 ? (
          <p className="px-2 py-4 text-center text-xs text-text-subtle">Nothing here</p>
        ) : (
          cards.map((item) => <BoardItemCard key={item.id} item={item} onOpen={onOpenCard} />)
        )}
      </div>
    </div>
  );
}

export interface BoardColumnsProps {
  readonly board: WorkBoardProjection;
  /** The lifecycle-ordered working states (unknown keys already appended last). */
  readonly states: readonly string[];
  /** The filtered slice — the cards; the counts stay whole-board. */
  readonly items: readonly WorkItemRow[];
  readonly onOpenCard: (itemId: string) => void;
}

/** The kanban board — working columns in lifecycle order, then the goal and dead exits. */
export function BoardColumns({
  board,
  states,
  items,
  onOpenCard,
}: BoardColumnsProps): React.JSX.Element {
  const byState = groupItemsByState(items);
  return (
    <section aria-label="Board columns" data-axis-view="kanban" className="overflow-x-auto pb-2">
      <div className="flex min-w-max gap-4">
        {states.map((state) => (
          <WorkingColumn
            key={state}
            state={state}
            count={board.countsByState[state] ?? 0}
            cards={byState.get(state) ?? []}
            onOpenCard={onOpenCard}
          />
        ))}
        {board.exits.map((exit) => (
          <ExitColumn key={exit.state} exit={exit} onOpenCard={onOpenCard} />
        ))}
      </div>
    </section>
  );
}
